import {
  ArrowUp,
  CircleNotch,
  Stop,
  UploadSimple,
} from "@phosphor-icons/react";
import { Button, Flex, IconButton, Text } from "@radix-ui/themes";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import {
  AskPunttArtifactProvider,
  type AskPunttArtifactContextValue,
} from "./AskPunttArtifactContext";
import { AgentSteps } from "./components/AgentSteps";
import { StillProcessingIndicator } from "./components/canvas/blocks/StillProcessingIndicator";
import { CanvasPane } from "./components/CanvasPane";
import { ChatMessage } from "./components/ChatMessage";
import { FeedSuggestionCard } from "./components/FeedSuggestionCard";
import { MiniStageBar } from "./components/MiniStageBar";
import { ProjectLoadingPane } from "./components/ProjectLoadingPane";
import { ProjectReviewsPanel } from "./components/ProjectReviewsPanel";
import { ActionStatus } from "./components/ProjectStatusPane";
import { ReviewChecklist } from "./components/ReviewChecklist";
import { SearchProgress } from "./components/SearchProgress";
import { StructuredResponse } from "./components/StructuredResponse";
import { SuggestedResponses } from "./components/SuggestedResponses";
import { ThinkingState } from "./components/ThinkingState";
import { MOCK_STRUCTURED_RESPONSE } from "./mockData";
import { BlockPreview, BlockRow, StyleGuideSection } from "./styleGuideHelpers";
import {
  AGENT_STEPS_COLLAPSED_GROUP,
  AGENT_STEPS_COMPLETE,
  AGENT_STEPS_CREATE_TICKET_IN_FLIGHT,
  AGENT_STEPS_STREAMING,
  CHAT_MESSAGE_STREAMING,
  CHAT_MESSAGE_WITH_TOOLS,
  FEED_SUGGESTION_RESOLVED,
  PROJECT_DATA_ACTIVE,
  PROJECT_DATA_BLOCKED,
  REVIEW_CHECKLIST_DYNAMIC_IN_PROGRESS,
  SEARCH_PROGRESS_MIXED,
  STAGE_BAR_STATES,
  THINKING_PHASE_MESSAGES,
} from "./styleGuideMocks";

import PendingComponent from "../../components/PendingComponent";

const noopArtifactCtx: AskPunttArtifactContextValue = {
  activeTicketId: null,
  activeCanvasMessageId: null,
  onActivateTicket: () => {},
  onActivateCanvas: () => {},
  isAnotherArtifactStreaming: false,
  allReviewTicketIds: [],
  canvasMessageIds: [],
};

function stageBarClasses(status: string): string {
  const base = "h-2.5 flex-1 rounded-full transition-all duration-300";
  switch (status) {
    case "done":
      return `${base} bg-malachite-500`;
    case "active":
      return `${base} bg-puntt-gold-9 animate-pulse`;
    case "needs_review":
      return `${base} bg-puntt-red-9 ring-2 ring-puntt-red-5`;
    case "blocked":
      return `${base} bg-puntt-red-9`;
    default:
      return `${base} bg-puntt-neutral-gray-5`;
  }
}

function ChatMessageDemo({
  message,
  isStreaming,
}: {
  message: typeof CHAT_MESSAGE_STREAMING;
  isStreaming: boolean;
}) {
  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: { retry: false },
          mutations: { retry: false },
        },
      }),
    [],
  );
  return (
    <QueryClientProvider client={queryClient}>
      <ChatMessage
        message={message}
        chatId="chat-style-guide"
        isStreaming={isStreaming}
        isLastAssistant
      />
    </QueryClientProvider>
  );
}

export function StyleGuideChatStatusSections() {
  const [structuredSections, setStructuredSections] = useState(4);

  return (
    <>
      <StyleGuideSection
        id="chat-thinking"
        title="ThinkingState — all phases"
        note="Bucket: passive wait. Used in ChatMessage (empty assistant) and chat.tsx (floating placeholder while waitingForAI)."
      >
        <div className="grid gap-4 md:grid-cols-2">
          {THINKING_PHASE_MESSAGES.map(
            ({ label, message, precedingUserHasAttachments }) => (
              <div key={label} className="space-y-2">
                <p className="text-xs text-puntt-neutral-gray-8">{label}</p>
                <ThinkingState
                  message={message}
                  precedingUserHasAttachments={precedingUserHasAttachments}
                />
              </div>
            ),
          )}
        </div>
      </StyleGuideSection>

      <StyleGuideSection
        id="chat-agent-steps"
        title="AgentSteps — streaming, complete, ticket progress, collapsed group"
        note="Bucket: active work. White accordion card; differs visually from ThinkingState inline spinner."
      >
        <AskPunttArtifactProvider value={noopArtifactCtx}>
          <div className="space-y-6">
            <div>
              <p className="mb-2 text-xs text-puntt-neutral-gray-8">
                Streaming
              </p>
              <AgentSteps steps={AGENT_STEPS_STREAMING} isStreaming />
            </div>
            <div>
              <p className="mb-2 text-xs text-puntt-neutral-gray-8">Complete</p>
              <AgentSteps steps={AGENT_STEPS_COMPLETE} isStreaming={false} />
            </div>
            <div>
              <p className="mb-2 text-xs text-puntt-neutral-gray-8">
                create_ticket in flight (progress bar)
              </p>
              <AgentSteps
                steps={AGENT_STEPS_CREATE_TICKET_IN_FLIGHT}
                isStreaming
              />
            </div>
            <div>
              <p className="mb-2 text-xs text-puntt-neutral-gray-8">
                Collapsed same-tool group
              </p>
              <AgentSteps
                steps={AGENT_STEPS_COLLAPSED_GROUP}
                isStreaming={false}
              />
            </div>
          </div>
        </AskPunttArtifactProvider>
      </StyleGuideSection>

      <StyleGuideSection
        id="chat-search"
        title="SearchProgress — mixed searching and done"
        note="Bucket: active work. Gray nested panel; appended after last tool segment during streaming."
      >
        <SearchProgress searchProgress={SEARCH_PROGRESS_MIXED} />
      </StyleGuideSection>

      <StyleGuideSection
        id="chat-streaming"
        title="ChatMessage — streaming cursor and tool interleave"
        note="Bucket: active work (cursor) plus tool/search panels. Streaming cursor is a 3px pulsing accent bar after markdown."
      >
        <div className="space-y-8">
          <div>
            <p className="mb-2 text-xs text-puntt-neutral-gray-8">
              Streaming text with cursor
            </p>
            <ChatMessageDemo message={CHAT_MESSAGE_STREAMING} isStreaming />
          </div>
          <div>
            <p className="mb-2 text-xs text-puntt-neutral-gray-8">
              Tool steps plus search progress (pending)
            </p>
            <ChatMessageDemo message={CHAT_MESSAGE_WITH_TOOLS} isStreaming />
          </div>
        </div>
      </StyleGuideSection>

      <StyleGuideSection
        id="chat-structured"
        title="StructuredResponse — progressive reveal"
        note="Bucket: resolved content reveal after turn completes. Sections fade up via animate-block-fade-up."
      >
        <div className="space-y-3">
          <Flex gap="2" wrap="wrap">
            {[1, 2, 3, 4].map((n) => (
              <Button
                key={n}
                size="1"
                variant={structuredSections === n ? "solid" : "soft"}
                data-testid={`style-guide-structured-sections-${n}`}
                onClick={() => setStructuredSections(n)}
                className="cursor-pointer"
              >
                {n} section{n === 1 ? "" : "s"}
              </Button>
            ))}
          </Flex>
          <StructuredResponse
            content={MOCK_STRUCTURED_RESPONSE}
            maxSections={structuredSections}
          />
        </div>
      </StyleGuideSection>

      <StyleGuideSection
        id="chat-composer"
        title="Composer controls — Stop, send spinner, attachment chip"
        note="Bucket: user-initiated / upload wait. Inline in chat.tsx and home.tsx — not yet shared components."
      >
        <Flex gap="4" wrap="wrap" align="center">
          <Button
            data-testid="style-guide-composer-stop"
            size="2"
            variant="soft"
            color="gray"
            className="cursor-pointer"
            aria-label="Stop generating"
          >
            <Stop size={14} weight="fill" />
            Stop
          </Button>
          <IconButton
            data-testid="style-guide-composer-send-spinner"
            size="2"
            variant="solid"
            color="blue"
            aria-label="Send message"
            disabled
          >
            <CircleNotch size={14} weight="bold" className="animate-spin" />
          </IconButton>
          <span
            className="inline-flex items-center gap-1.5 rounded-md bg-puntt-neutral-gray-3 px-2 py-1"
            data-testid="style-guide-attached-file-chip"
          >
            <CircleNotch
              size={12}
              weight="bold"
              className="shrink-0 animate-spin text-puntt-neutral-gray-9"
            />
            <Text size="1">brief.pdf</Text>
          </span>
        </Flex>
      </StyleGuideSection>

      <StyleGuideSection
        id="chat-suggestions"
        title="SuggestedResponses"
        note="Bucket: post-response affordance (not loading, but shares animate-block-fade-up motion)."
      >
        <SuggestedResponses
          suggestions={[
            "Show compliant alternatives",
            "Draft a revision email",
            "Compare to last approved version",
          ]}
          onSelect={() => {}}
        />
      </StyleGuideSection>
    </>
  );
}

export function StyleGuideReviewProjectSections({
  canvasDemo,
  onCanvasDismiss,
  showLoadingPane,
  onToggleLoadingPane,
}: {
  canvasDemo: React.ComponentProps<typeof CanvasPane>["canvas"];
  onCanvasDismiss: () => void;
  showLoadingPane: boolean;
  onToggleLoadingPane: () => void;
}) {
  return (
    <>
      <StyleGuideSection
        id="review-checklist"
        title="ReviewChecklist — in progress, complete, parallel"
        note="Bucket: active work during AI review. Floating widget in chat.tsx; staggered item reveal."
      >
        <div className="space-y-6">
          <div>
            <p className="mb-2 text-xs text-puntt-neutral-gray-8">
              In progress (polling)
            </p>
            <ReviewChecklist
              workflowType="compliance_review"
              isReviewPolling
              dynamicItems={REVIEW_CHECKLIST_DYNAMIC_IN_PROGRESS}
            />
          </div>
          <div>
            <p className="mb-2 text-xs text-puntt-neutral-gray-8">Complete</p>
            <ReviewChecklist
              workflowType="compliance_review"
              reviewComplete
              dynamicItems={REVIEW_CHECKLIST_DYNAMIC_IN_PROGRESS}
            />
          </div>
          <div>
            <p className="mb-2 text-xs text-puntt-neutral-gray-8">
              Parallel tickets (3)
            </p>
            <ReviewChecklist
              workflowType="artwork_review"
              isReviewPolling
              parallelTicketCount={3}
            />
          </div>
        </div>
      </StyleGuideSection>

      <StyleGuideSection
        id="review-action-status"
        title="ActionStatus — project pane banners"
        note="Bucket: passive wait, human action, resolved, error. Uses ReviewStatusCallout shell."
      >
        <div className="space-y-3">
          <ActionStatus
            routing={[]}
            hasTicket={false}
            stages={[{ name: "Brand", status: "active", color: "gray" }]}
          />
          <ActionStatus
            routing={[]}
            hasTicket
            stages={[
              { name: "Brand", status: "pending", color: "gray" },
              { name: "Legal", status: "pending", color: "gray" },
            ]}
          />
          <ActionStatus
            routing={[]}
            hasTicket={false}
            stages={[
              { name: "Regulatory", status: "needs_review", color: "amber" },
            ]}
          />
          <ActionStatus
            routing={[]}
            hasTicket={false}
            stages={[{ name: "Regulatory", status: "blocked", color: "red" }]}
          />
          <ActionStatus
            routing={[]}
            hasTicket={false}
            stages={[
              { name: "Brand", status: "pending", color: "gray" },
              { name: "Legal", status: "pending", color: "gray" },
            ]}
            onSendMessage={() => {}}
          />
        </div>
      </StyleGuideSection>

      <StyleGuideSection
        id="review-stage-bars"
        title="Stage progress bars — project card and status pane"
        note="Bucket: passive progress. Gold pulse = active; malachite = done; red = needs_review or blocked."
      >
        <div className="space-y-6">
          <div>
            <p className="mb-2 text-xs text-puntt-neutral-gray-8">
              MiniStageBar (projects list card)
            </p>
            <MiniStageBar stages={STAGE_BAR_STATES} />
          </div>
          <div>
            <p className="mb-2 text-xs text-puntt-neutral-gray-8">
              Status pane bars (ProjectStatusPane)
            </p>
            <Flex gap="1">
              {STAGE_BAR_STATES.filter((s) => s.name !== "Final").map(
                (stage) => (
                  <div
                    key={stage.name}
                    className={stageBarClasses(stage.status)}
                    title={`${stage.name}: ${stage.status}`}
                  />
                ),
              )}
            </Flex>
          </div>
        </div>
      </StyleGuideSection>

      <StyleGuideSection
        id="review-ticket-status"
        title="Ticket / asset review status text"
        note="Bucket: passive wait in TicketViewPane and AssetViewerPane headers."
      >
        <Flex direction="column" gap="2">
          <Text
            size="1"
            className="animate-pulse italic text-puntt-accent-9"
            data-testid="style-guide-ticket-status-in-progress"
          >
            Reviewing brand guidelines
          </Text>
          <Text
            size="1"
            className="italic text-malachite-700"
            data-testid="style-guide-ticket-status-complete"
          >
            Review complete
          </Text>
        </Flex>
      </StyleGuideSection>

      <StyleGuideSection
        id="review-canvas-staging"
        title="CanvasPane — staging overlay"
        note="Bucket: active work while canvas blocks update. Pass isLoading with existing blocks."
      >
        <div className="h-[320px] overflow-hidden rounded-lg border border-puntt-neutral-gray-5">
          <CanvasPane
            canvas={canvasDemo}
            onDismiss={onCanvasDismiss}
            skillName="Risk Review"
            isLoading
          />
        </div>
      </StyleGuideSection>

      <StyleGuideSection
        id="review-still-processing"
        title="Still processing file preview"
        note="Bucket: passive wait for signed URL. StillProcessingFallback polls; this is the presentational shell."
      >
        <StillProcessingIndicator
          fileName="brand-guidelines.pdf"
          onRetry={() => {}}
        />
      </StyleGuideSection>

      <StyleGuideSection
        id="review-loading-pane"
        title="ProjectLoadingPane"
        note="Bucket: passive wait while project side pane bootstraps."
      >
        <div className="h-64 overflow-hidden rounded-lg border border-puntt-neutral-gray-5">
          <ProjectLoadingPane />
        </div>
        <button
          type="button"
          data-testid="style-guide-toggle-loading-pane"
          className="mt-3 cursor-pointer rounded-md bg-puntt-neutral-gray-3 px-3 py-1.5 text-xs font-medium text-puntt-neutral-gray-11 hover:bg-puntt-neutral-gray-4"
          onClick={onToggleLoadingPane}
        >
          {showLoadingPane ? "Hide" : "Show"} full-height loading pane
        </button>
        {showLoadingPane && (
          <div className="mt-3 h-[480px] overflow-hidden rounded-lg border border-puntt-neutral-gray-5">
            <ProjectLoadingPane />
          </div>
        )}
      </StyleGuideSection>
    </>
  );
}

export function StyleGuideAdjacentSections() {
  return (
    <>
      <StyleGuideSection
        id="adjacent-spinners"
        title="Home and campaign send / upload spinners"
        note="Bucket: user-initiated wait on home.tsx and CampaignNewChatDraft."
      >
        <Flex gap="4" wrap="wrap" align="center">
          <Button
            size="2"
            variant="solid"
            color="blue"
            disabled
            data-testid="style-guide-home-send-spinner"
            aria-label="Ask Puntt"
          >
            <CircleNotch size={14} weight="bold" className="animate-spin" />
            Ask Puntt
          </Button>
          <IconButton
            size="2"
            variant="solid"
            color="blue"
            disabled
            aria-label="Send message"
            data-testid="style-guide-campaign-send-spinner"
          >
            <ArrowUp size={14} weight="bold" className="animate-spin" />
          </IconButton>
        </Flex>
      </StyleGuideSection>

      <StyleGuideSection
        id="adjacent-mini-stage"
        title="MiniStageBar — project list card"
        note={`Bottom status copy comes from deriveBottomStatus in projects.tsx. Sample: active project "${PROJECT_DATA_ACTIVE.stages?.[0]?.name}" in progress.`}
      >
        <MiniStageBar stages={PROJECT_DATA_ACTIVE.stages ?? []} />
        <p className="mt-2 text-xs text-puntt-neutral-gray-9">
          Blocked example uses red segments:
        </p>
        <MiniStageBar stages={PROJECT_DATA_BLOCKED.stages ?? []} />
      </StyleGuideSection>

      <StyleGuideSection
        id="adjacent-feed"
        title="FeedSuggestionCard — resolved"
        note="Bucket: resolved. Collapses to a status line after user action."
      >
        <FeedSuggestionCard
          event={FEED_SUGGESTION_RESOLVED}
          onResolve={() => {}}
        />
      </StyleGuideSection>

      <StyleGuideSection
        id="adjacent-pending"
        title="Route pending — PendingComponent"
        note="Bucket: passive wait on route transitions (chatRoute, chatsRoute)."
      >
        <div className="h-32 overflow-hidden rounded-lg border border-puntt-neutral-gray-5">
          <PendingComponent />
        </div>
      </StyleGuideSection>

      <StyleGuideSection
        id="adjacent-drop-zone"
        title="AskPunttDropZone — drag overlay (static replica)"
        note="Bucket: in-progress drag state. Real overlay appears on external file drag; replica shows markup."
      >
        <div className="relative h-40 rounded-lg border border-puntt-neutral-gray-5 bg-puntt-neutral-gray-1">
          <Text size="2" className="p-4 text-puntt-neutral-gray-9">
            Composer content behind overlay
          </Text>
          <div
            className="pointer-events-none absolute inset-0 z-50 flex items-end justify-center bg-gradient-to-t from-puntt-accent-5/80 via-puntt-accent-3/50 to-transparent backdrop-blur-sm"
            data-testid="style-guide-drop-overlay-replica"
          >
            <div className="m-6 flex flex-col items-center gap-4 rounded-xl border-2 border-dashed border-puntt-accent-9 px-8 py-6 text-puntt-accent-11">
              <UploadSimple size={32} aria-hidden="true" />
              <Text size="5" weight="medium">
                Drop files to attach
              </Text>
            </div>
          </div>
        </div>
      </StyleGuideSection>

      <StyleGuideSection
        id="adjacent-reviews-loading"
        title="ProjectReviewsPanel — loading spinner"
        note="Bucket: passive wait while reviews list fetches."
      >
        <div className="h-48 overflow-hidden rounded-lg border border-puntt-neutral-gray-5">
          <ProjectReviewsPanel isLoading />
        </div>
      </StyleGuideSection>

      <StyleGuideSection
        id="adjacent-chat-skeleton"
        title="Chat history loading skeleton"
        note="Bucket: passive wait on initial chat load (chat.tsx)."
      >
        <div className="space-y-3">
          <div className="h-16 animate-pulse rounded-lg bg-puntt-neutral-gray-4" />
          <div className="h-16 animate-pulse rounded-lg bg-puntt-neutral-gray-4" />
          <div className="h-16 animate-pulse rounded-lg bg-puntt-neutral-gray-4" />
        </div>
      </StyleGuideSection>
    </>
  );
}

export function StyleGuideDesignTokensSection() {
  return (
    <StyleGuideSection
      id="design-tokens"
      title="Design tokens — animations and status colors"
      note="Reference for standardization. Review callouts use Radix stock colors; chat activity uses puntt-neutral panels."
    >
      <div className="space-y-6 text-sm text-puntt-neutral-gray-11">
        <div>
          <p className="mb-2 font-medium text-puntt-neutral-gray-12">
            Animations
          </p>
          <ul className="list-inside list-disc space-y-1 text-xs">
            <li>
              <code className="text-puntt-accent-11">
                animate-thinking-pulse
              </code>{" "}
              — ThinkingState phase 0 avatar
            </li>
            <li>
              <code className="text-puntt-accent-11">
                animate-block-fade-up
              </code>{" "}
              — agent steps, checklist items, structured sections, canvas blocks
            </li>
            <li>
              <code className="text-puntt-accent-11">
                animate-section-highlight
              </code>{" "}
              — canvas section navigation highlight
            </li>
            <li>
              <code className="text-puntt-accent-11">animate-spin</code> on{" "}
              CircleNotch — primary spinner primitive
            </li>
          </ul>
        </div>
        <div>
          <p className="mb-2 font-medium text-puntt-neutral-gray-12">
            Spinner and progress colors
          </p>
          <BlockRow>
            <BlockPreview>
              <div className="flex items-center gap-2">
                <CircleNotch
                  size={16}
                  className="animate-spin text-puntt-accent-9"
                />
                <span className="text-xs">puntt-accent-9 (chat activity)</span>
              </div>
            </BlockPreview>
            <BlockPreview>
              <div className="flex items-center gap-2">
                <span className="size-2.5 animate-pulse rounded-full bg-puntt-gold-9" />
                <span className="text-xs">
                  puntt-gold-9 pulse (active stage)
                </span>
              </div>
            </BlockPreview>
            <BlockPreview>
              <div className="flex items-center gap-2 text-malachite-700">
                <span className="text-xs font-medium">
                  malachite-700 (done)
                </span>
              </div>
            </BlockPreview>
          </BlockRow>
        </div>
        <div>
          <p className="mb-2 font-medium text-puntt-neutral-gray-12">
            Semantic buckets (target for unification)
          </p>
          <ul className="list-inside list-disc space-y-1 text-xs">
            <li>Passive wait — AI working, nothing for user to do</li>
            <li>Active work — tools, search, streaming, canvas staging</li>
            <li>Human action — needs review, notify CTAs</li>
            <li>
              Resolved — green callouts, complete checklist, review complete
              text
            </li>
            <li>Error / blocked — red callouts and stage segments</li>
          </ul>
        </div>
      </div>
    </StyleGuideSection>
  );
}
